import type { UseQueryResult } from "@tanstack/react-query";
import { Link } from "react-router";

import { QueryErrorState } from "@/api/QueryErrorState";
import { isTerminalQueryError } from "@/api/queryClient";
import type {
  AdoptionPin,
  LibraryAsset,
  LibraryAssetList,
  ProjectAdoptions,
} from "@/api/library";
import { ASSET_TYPE_LABELS, libraryAssetHref } from "@/lib/librarySections";
import { Badge } from "@/ui/Badge";
import { Notice } from "@/ui/Notice";
import { EmptyState, LoadingState, StaleDataNotice } from "@/ui/states";

import { consequenceRendererFor } from "./consequences";

/**
 * What this project's pins mean, on the project's own Summary (#373).
 *
 * THE GENERIC HALF. Everything here is true of every pin regardless of type:
 * which asset it names, at which version, whether the library has moved past
 * it, and whether the asset has since been archived. What a pin DOES is
 * type-specific and comes from a registered renderer (`consequences.ts`); a
 * type with no renderer gets an explicit statement rather than silence, which
 * is what makes the seam visible instead of merely asserted.
 *
 * ONE ADOPTIONS QUERY. The pin set is the caller's — `ClinicalSummary` holds
 * the single `useProjectAdoptionsQuery` and passes it down, and renderers
 * receive one pin each as a prop. Nothing in this directory reads adoption
 * state a second time, which is #373 AC 4 and the reason #372's control
 * surface and this one cannot disagree about what a project pins.
 *
 * THE CATALOG IS NOT ADOPTION STATE. `AdoptionPin` carries an `assetId`, a
 * `version` and — since #615 — the `assetType` the service joins onto it, and
 * no name, so a surface that showed only the pin would show a clinician an
 * opaque identifier and a family. The catalog read supplies the name and the
 * current head — the same join `MatrixPinning` performs — and it is read with
 * archived rows INCLUDED, because an archived asset keeps its existing pins by
 * contract and a project pinning one must still be told what it pins.
 *
 * AND A CATALOG THAT DID NOT LOAD DOES NOT ERASE THE PIN LIST (#615). The pin
 * set is the subject here; the catalog names it. When the catalog read fails,
 * the failure is stated once, above the list, and every pin is still listed
 * by what the pin set itself vouches for — id, exact version, family, and
 * therefore its exact library page — because the ticket's failure-state
 * criterion is that the way back to the authoritative asset stays while the
 * stored pin is known. Nothing the catalog uniquely supplies is guessed.
 *
 * READ-ONLY. No control here writes: adoption, upgrade, diff and safety-lock
 * controls belong to #372, and a second place to move a pin is how two
 * surfaces come to disagree about the set.
 */
/**
 * The asset ids this surface renders a pin card for, under the reads it was
 * given — its render gates below, stated once as a value.
 *
 * ONE ASSET, ONE `View in Clinical library` PER PAGE (#570, extended to
 * navigation by #615). `ClinicalSummary` mounts the section-scoped adoption
 * panel ABOVE this list, and a pinned Summary prompt is an adopted item there
 * and a card here — two identical links for one asset, on one page, until the
 * panel is told which pins a card already links. That is this set: every pin
 * exactly when the cards render, and EMPTY whenever the PIN read does not
 * produce cards (pending or failed). Catalog state does not gate a route that
 * is completely determined by the stored pin. `PinConsequences.test.tsx` pins
 * the set against the render, so the two cannot drift.
 */
export function pinCardAssetIds(
  adoptions: UseQueryResult<ProjectAdoptions>,
): ReadonlySet<string> {
  if (!adoptions.isSuccess) {
    return new Set();
  }
  return new Set(adoptions.data.items.map((pin) => pin.assetId));
}

export function PinConsequences({
  projectId,
  adoptions,
  catalog,
}: {
  projectId: string;
  /** The Summary's single adoptions query. Not re-fetched here. */
  adoptions: UseQueryResult<ProjectAdoptions>;
  /** The catalog naming those pins, archived rows included. */
  catalog: UseQueryResult<LibraryAssetList>;
}) {
  if (adoptions.isPending) {
    return <LoadingState label="Loading this project's pins" rows={2} />;
  }

  if (adoptions.isError) {
    return (
      <QueryErrorState
        error={adoptions.error}
        subject="Adoptions"
        title="This project's pins could not be loaded"
        description="Nothing below describes what this project has adopted. An empty list here would say it has adopted nothing, which is not what the Editor Service reported."
        onRetry={() => void adoptions.refetch()}
      />
    );
  }

  const pins = adoptions.data.items;
  const terminalCatalogFailure =
    catalog.isError && isTerminalQueryError(catalog.error);
  // Match the repository's cache policy: a transient failed refresh may show
  // its last successful response with a stale notice; a terminal refusal must
  // not expose that older response.
  const readableCatalog = terminalCatalogFailure ? undefined : catalog.data;
  const catalogState = catalog.isSuccess
    ? "current"
    : catalog.isPending
      ? "loading"
      : readableCatalog
        ? "stale"
        : "failed";
  const catalogNotice = catalog.isPending ? (
    <Notice>
      The shared library catalog is still loading. Every stored pin and its
      exact library page remain available below; names, library-head
      comparisons, and consequences appear after the catalog answers.
    </Notice>
  ) : catalog.isError && catalog.data && !terminalCatalogFailure ? (
    <StaleDataNotice
      updatedAt={catalog.dataUpdatedAt}
      onRetry={() => void catalog.refetch()}
    />
  ) : catalog.isError ? (
    <QueryErrorState
      error={catalog.error}
      subject="Library assets"
      title="The pinned assets could not be named"
      description={`This project holds ${pins.length} ${
        pins.length === 1 ? "pin" : "pins"
      }, but the library catalog that names them did not load. Each is listed below by its stored identity — the exact version, its family, and its library page — without its name, its library head, or what it changes.`}
      onRetry={() => void catalog.refetch()}
    />
  ) : null;

  if (pins.length === 0) {
    return (
      <EmptyState
        title="This project pins nothing yet"
        description="Its clinical configuration is the base pack alone. Pins are added from the shared library, and each one is recorded here with what it changes."
      />
    );
  }

  const assetsById = new Map(
    (readableCatalog?.items ?? []).map((asset) => [asset.id, asset]),
  );

  return (
    <div className="space-y-4">
      {catalogNotice}
      <p className="text-sm text-muted-foreground">
        {pins.length === 1
          ? "This project pins one shared asset."
          : `This project pins ${pins.length} shared assets.`}{" "}
        Each pin names an immutable version, so publishing a newer one never
        moves it. Pin set {adoptions.data.adoptionsVersion}.
      </p>

      <ul className="space-y-4">
        {pins.map((pin) => (
          <li key={pin.assetId}>
            <PinCard
              projectId={projectId}
              pin={pin}
              asset={assetsById.get(pin.assetId)}
              catalogState={catalogState}
            />
          </li>
        ))}
      </ul>
    </div>
  );
}

function PinCard({
  projectId,
  pin,
  asset,
  catalogState,
}: {
  projectId: string;
  pin: AdoptionPin;
  asset: LibraryAsset | undefined;
  /** Which catalog state explains an absent asset. */
  catalogState: "current" | "loading" | "stale" | "failed";
}) {
  if (!asset) {
    // The catalog did not name this pin — because it did not load, or because
    // it does not list the asset — and the two are told apart, because only
    // one of them is a statement about the library. Stated, not hidden: a pin
    // the clinician cannot see is a pin they cannot reason about, and dropping
    // the row would make the list disagree with the set. What the PIN SET
    // vouches for is still rendered: the family, the exact version, and the
    // exact library page (#615), all from the stored pin and nothing guessed.
    return (
      <div className="space-y-2 rounded-md border border-border p-4">
        <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
          <span className="wrap-anywhere min-w-0 flex-1 font-mono text-sm text-foreground">
            {pin.assetId}
          </span>
          <Badge tone="outline">{ASSET_TYPE_LABELS[pin.assetType]}</Badge>
          <Badge tone="accent">Pinned {pin.version}</Badge>
          <Link
            to={libraryAssetHref(pin.assetType, pin.assetId)}
            className="wrap-anywhere text-sm font-medium text-accent-foreground underline underline-offset-4"
          >
            {`View ${pin.assetId} in Clinical library`}
          </Link>
        </div>
        <p className="text-sm text-muted-foreground">
          {catalogState === "current"
            ? "The shared library catalog does not list this asset, so its name and what it governs cannot be shown until the catalog and the pin agree."
            : catalogState === "stale"
              ? "The last successful catalog response did not list this asset. Its current name, library head, and consequences are not claimed from stale data."
              : catalogState === "loading"
                ? "The shared library catalog is still loading, so this pin's name, its library head, and what it changes are not shown yet."
                : "The shared library catalog did not load, so this pin's name, its library head, and what it changes are not shown."}
        </p>
      </div>
    );
  }

  const renderer = consequenceRendererFor(asset.assetType);
  const behind = pin.version !== asset.latestVersion;

  return (
    <div className="rounded-md border border-border">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2 border-b border-border px-4 py-3">
        <span className="min-w-0 flex-1 text-sm font-medium text-foreground">
          {asset.name}
        </span>
        <Badge tone="accent">Pinned {pin.version}</Badge>
        {behind ? (
          <Badge tone="warning">
            Library head is {asset.latestVersion}
          </Badge>
        ) : null}
        {asset.capabilities.archived ? <Badge tone="outline">Archived</Badge> : null}
        {/*
          The exact library page of the pinned asset (#615), from the
          catalog's own `assetType` and the pin's stored id — the same route
          the unnamed-pin card above builds from the pin's own `assetType`,
          because the two state one fact (the composite FK keeps them
          consistent) and either suffices. A guessed slug is still never
          built: the family always comes from a server response, never from
          where the card renders. READ-ONLY holds: this card still writes
          nothing — navigation to shared clinical content is somewhere to
          look, not a control over the pin set. An archived asset keeps the
          link, because archive preserves existing pins and their resolution
          by contract.
        */}
        <Link
          to={libraryAssetHref(asset.assetType, pin.assetId)}
          className="text-sm font-medium text-accent-foreground underline underline-offset-4"
        >
          {`View ${asset.name} in Clinical library`}
        </Link>
      </div>

      <div className="space-y-2 px-4 py-3">
        {renderer ? (
          <>
            <p className="text-sm font-medium text-foreground">
              {renderer.heading}
            </p>
            <renderer.Component projectId={projectId} pin={pin} asset={asset} />
          </>
        ) : (
          // The seam's honest default. Naming the type rather than showing an
          // empty block is the difference between "no consequence is rendered
          // for this type yet" and "this pin does nothing", and only the first
          // is true.
          <p className="text-sm text-muted-foreground">
            This project pins {asset.name} at {pin.version}. What a{" "}
            <span className="font-mono">{asset.assetType}</span> pin changes is
            not rendered on this screen yet — #373 delivers the alert matrix,
            and each remaining type arrives with its own renderer.
          </p>
        )}
      </div>
    </div>
  );
}
